import React, { ComponentType, lazy } from 'react';

interface LazyWithRetryOptions {
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Enhanced lazy loading with retry logic for failed module imports
 * Helps handle network errors when loading lazy-loaded components
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options: LazyWithRetryOptions = {}
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, retryDelay = 1000 } = options;

  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      let retries = 0;

      const attemptImport = async () => {
        try {
          const module = await importFunc();
          resolve(module);
        } catch (error) {
          // Check if it's a network/fetch error
          const isNetworkError =
            error instanceof TypeError &&
            (error.message.includes('Failed to fetch') ||
              error.message.includes('Failed to load') ||
              error.message.includes('dynamically imported module'));

          // Check if user is offline
          const isOffline = !navigator.onLine;

          if ((isNetworkError || isOffline) && retries < maxRetries) {
            retries++;
            console.warn(
              `⚠️ Failed to load module (attempt ${retries}/${maxRetries}). Retrying...`,
              error
            );

            // Wait before retrying
            setTimeout(attemptImport, retryDelay * retries);
          } else {
            // If all retries failed or it's not a network error, reject with a clear message
            const errorMessage = isOffline
              ? 'Cannot load module: No internet connection'
              : error instanceof Error
              ? error.message
              : 'Unknown error loading module';

            reject(
              new Error(
                `Failed to fetch dynamically imported module after ${retries} retries: ${errorMessage}`
              )
            );
          }
        }
      };

      attemptImport();
    });
  });
}

/**
 * Hook to detect online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
